import React from 'react';
import { Alert, LoadingSpinner } from '@shared/components';
import type {
    DerivedScheduleResponse,
    DerivedScheduleServiceEntry,
} from '../../../types/derivedSchedule.types';

interface DerivedSchedulePlaceholderProps {
    className?: string;
    isLoading: boolean;
    isDerivedLoading: boolean;
    isDerivedError: boolean;
    error: unknown;
    derivedSchedule: DerivedScheduleResponse | undefined;
    derivedRows: DerivedScheduleServiceEntry[];
    derivedPricingResolved: boolean;
    children: React.ReactNode;
}

// The loading/error/empty-schedule guards that gate DerivedServicesPanel's
// main content. Renders `children` only once every guard has passed.
export const DerivedSchedulePlaceholder: React.FC<DerivedSchedulePlaceholderProps> = ({
    className,
    isLoading,
    isDerivedLoading,
    isDerivedError,
    error,
    derivedSchedule,
    derivedRows,
    derivedPricingResolved,
    children,
}) => {
    // Wait for both the derived schedule and the service catalog: rows are
    // only priced once the catalog resolves, and syncing/rendering them
    // before then would show/persist zero-priced services.
    if (isDerivedLoading || isLoading) {
        return (
            <div className={`bg-mp-surface rounded-xl p-6 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                <LoadingSpinner message="Loading service schedule..." className="py-12" />
            </div>
        );
    }

    // Distinguish the two independent failure sources so the message
    // reflects what actually failed. `isDerivedError` is the derived-schedule
    // query (useDerivedSchedule); `error` is the service-catalog/pricing
    // query (useAvailableServices). Conflating them showed "service
    // schedule" even when only the catalog failed, which misled debugging.
    // When both fail, name both so neither cause is hidden.
    if (isDerivedError || error) {
        const errorMessage =
            isDerivedError && error
                ? 'Error loading the service schedule and pricing. Please try again.'
                : isDerivedError
                  ? 'Error loading the service schedule. Please try again.'
                  : 'Error loading service pricing. Please try again.';
        return (
            <div className={`bg-mp-surface rounded-xl p-6 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                <div role="alert" className="text-center py-8">
                    <p className="text-mp-error">{errorMessage}</p>
                </div>
            </div>
        );
    }

    // An empty authoritative schedule is only an error when the plan
    // expects at least one service. The backend bounds invariant allows
    // `default_service_count === 0` (0 <= min <= default <= max), in which
    // case an empty `services[]` is valid and the (empty) locked/guided
    // list renders below — so only trip the guard when the resolved bounds
    // default is > 0, or when bounds are null (genuinely misconfigured /
    // ambiguous / no-match).
    const authoritativeEmpty = (derivedSchedule?.services.length ?? 0) === 0;
    const boundsDefault = derivedSchedule?.bounds?.default ?? null;
    if (authoritativeEmpty && boundsDefault !== 0) {
        const candidateCount = derivedSchedule?.candidatePlans.length ?? 0;
        const guardMessage =
            candidateCount > 1
                ? 'Ambiguous plan configuration — multiple candidate plans match this vehicle.'
                : candidateCount === 0
                  ? 'No plan matches this vehicle.'
                  : 'This service plan is misconfigured. Contact an administrator.';

        return (
            <div className={`bg-mp-surface rounded-xl p-6 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                <Alert variant="warning">{guardMessage}</Alert>
            </div>
        );
    }

    // Coherent zero-service schedule (bounds.default === 0 with no derived
    // services). The schedule itself is internally valid, and — as of #872 —
    // the quote wizard's step gate (`QuoteContext`'s 'configuration' arm,
    // via `minimumConfiguredServices`) now honours `bounds.min === 0` and
    // does NOT block proceeding, so this is no longer a dead end. Still
    // worth an explicit informational message rather than an empty panel
    // with no derived rows and no add-form (locked mode has neither), so
    // the block is kept — just re-purposed from "you are stuck" to "this is
    // expected". `role="status"` (informational), not `role="alert"` — the
    // genuinely misconfigured/ambiguous/no-match branch above keeps that.
    if (authoritativeEmpty && boundsDefault === 0) {
        return (
            <div className={`bg-mp-surface rounded-xl p-6 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                <Alert variant="warning" role="status">
                    This plan includes no scheduled services. You can continue to the next
                    step.
                </Alert>
            </div>
        );
    }

    // Catalog settled but one or more derived services have no catalog
    // entry: surface it explicitly rather than showing or persisting
    // zero-priced rows.
    if (derivedRows.length > 0 && !derivedPricingResolved) {
        return (
            <div className={`bg-mp-surface rounded-xl p-6 ${className}`}>
                <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                    1. Configure Core Services
                </h2>
                <Alert variant="warning">
                    Pricing is unavailable for one or more scheduled services. Contact an
                    administrator.
                </Alert>
            </div>
        );
    }

    return <>{children}</>;
};
